import { cn } from "@/lib/cn";

export interface StreakHeatmapProps {
  /** ISO date (YYYY-MM-DD) of the user's last active day. */
  lastDay: string | null;
  /** Current consecutive-day streak length. */
  streak: number;
  /** Number of weeks of history rendered. Defaults to 12. */
  weeks?: number;
  /** Show the legend row below the grid. */
  showLegend?: boolean;
  className?: string;
}

/** Parses a YYYY-MM-DD ISO date into a UTC Date. Returns null on parse
 * failure so callers can degrade gracefully. */
function parseISO(value: string | null): Date | null {
  if (!value) return null;
  const parts = value.split("-");
  if (parts.length !== 3) return null;
  const y = Number.parseInt(parts[0] ?? "", 10);
  const m = Number.parseInt(parts[1] ?? "", 10);
  const d = Number.parseInt(parts[2] ?? "", 10);
  if (!y || !m || !d) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addDaysUTC(base: Date, delta: number): Date {
  const copy = new Date(base.getTime());
  copy.setUTCDate(copy.getUTCDate() + delta);
  return copy;
}

/** GitHub-style contribution heatmap covering the last `weeks` weeks.
 * Days within the active streak window (ending at `lastDay`) are painted
 * in brand color. Inactive days sit at bg-white/5 so the whole grid
 * remains readable on the dark Glow theme. */
export function StreakHeatmap({
  lastDay,
  streak,
  weeks = 12,
  showLegend = true,
  className,
}: StreakHeatmapProps) {
  const last = parseISO(lastDay);
  const cellSize = 10;
  const gap = 3;
  const totalDays = weeks * 7;
  const anchor = last ?? new Date();
  // Start of the grid is `weeks * 7 - 1` days before anchor.
  const start = addDaysUTC(anchor, -(totalDays - 1));

  const activeSet = new Set<string>();
  if (last && streak > 0) {
    for (let i = 0; i < streak; i++) {
      const d = addDaysUTC(last, -i);
      activeSet.add(d.toISOString().slice(0, 10));
    }
  }

  const width = weeks * (cellSize + gap) - gap;
  const height = 7 * (cellSize + gap) - gap;

  const cells: {
    x: number;
    y: number;
    iso: string;
    active: boolean;
    intensity: number;
  }[] = [];
  for (let w = 0; w < weeks; w++) {
    for (let d = 0; d < 7; d++) {
      const idx = w * 7 + d;
      const date = addDaysUTC(start, idx);
      const iso = date.toISOString().slice(0, 10);
      const active = activeSet.has(iso);
      // Fade older active days slightly so the tail of the streak
      // reads visually as older context.
      const ageFromEnd = last
        ? Math.floor(
            (last.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
          )
        : 0;
      const intensity = active
        ? ageFromEnd < 3
          ? 1
          : ageFromEnd < 7
            ? 0.82
            : 0.62
        : 0;
      cells.push({
        x: w * (cellSize + gap),
        y: d * (cellSize + gap),
        iso,
        active,
        intensity,
      });
    }
  }

  return (
    <div
      className={cn("inline-flex flex-col gap-1.5 text-text-4", className)}
      aria-label={`Racha de ${streak} días`}
    >
      <svg
        width={width}
        height={height}
        role="img"
        aria-hidden
        style={{ shapeRendering: "crispEdges" }}
      >
        {cells.map((c) => (
          <rect
            key={c.iso}
            x={c.x}
            y={c.y}
            width={cellSize}
            height={cellSize}
            rx={2}
            ry={2}
            fill={c.active ? "#a78bfa" : "rgba(255,255,255,0.06)"}
            fillOpacity={c.active ? c.intensity : 1}
          >
            <title>{`${c.iso} — ${c.active ? "activo" : "inactivo"}`}</title>
          </rect>
        ))}
      </svg>
      {showLegend && (
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider">
          <span>menos</span>
          <span
            className="inline-block rounded-sm"
            style={{ width: 9, height: 9, background: "rgba(255,255,255,0.06)" }}
          />
          <span
            className="inline-block rounded-sm"
            style={{ width: 9, height: 9, background: "#a78bfa", opacity: 0.62 }}
          />
          <span
            className="inline-block rounded-sm"
            style={{ width: 9, height: 9, background: "#a78bfa", opacity: 0.82 }}
          />
          <span
            className="inline-block rounded-sm"
            style={{ width: 9, height: 9, background: "#a78bfa" }}
          />
          <span>más</span>
        </div>
      )}
    </div>
  );
}
